import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {motion, AnimatePresence} from 'motion/react';
import {r} from "@/utils/responsive";
import DisabledWrapper from "@/components/DisabledWrapper";
import OracleVideoAvatar from "@/assets/video/oracle_avatar_348x348.mp4"
import ButtonReforge from "@/components/ButtonReforge";
import ButtonSpan from "@/components/ButtonSpan";
import ShortcutText from "@/components/ShortcutText";
import ShortcutReforge from "@/components/ShortcutReforge";
import {useShortcuts} from "@/hooks/useShortcuts";
import ReactPlayerWrapper from "@/components/ReactPlayerWrapper";
import {S3_FILES} from "@/config/s3_files";
import {getModifierKey} from "@/utils/platform";

interface BotHelperButtonProps extends React.HTMLAttributes<HTMLButtonElement> {
    hint?: string[] | null;
}

const BotHelperModal: React.FC<{ isOpen: boolean; onClose: () => void; hint?: string[] | null }> = ({
                                                                                                        isOpen,
                                                                                                        onClose,
                                                                                                        hint
                                                                                                    }) => {
    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{opacity: 0}}
                    animate={{opacity: 1}}
                    exit={{opacity: 0}}
                    transition={{duration: 0.3}}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: '#000E3390',
                        display: 'flex',
                        alignItems: 'start',
                        justifyContent: 'center',
                        zIndex: 1000,
                        paddingTop: r.vmax(210),
                    }}
                    onClick={onClose}
                >
                    <motion.div
                        initial={{translateY: 40, opacity: 0}}
                        animate={{translateY: 0, opacity: 1}}
                        exit={{translateY: 40, opacity: 0}}
                        transition={{duration: 0.3, ease: "easeOut"}}
                        style={{
                            background: '#272E64',
                            width: r.vmax(700),
                            padding: r.vmax(40),
                            borderRadius: r.vmax(60),
                            color: 'white'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: r.vmax(32),
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: r.vmax(28),
                            }}>
                                <div style={{
                                    position: 'relative',
                                    width: r.vmax(88),
                                    height: r.vmax(88),
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    boxShadow: `0px 3.4321px 20.5926px #537EFF, 0px 3.4321px 46.3333px #537EFF`
                                }}>
                                    <DisabledWrapper style={{
                                        zIndex: 1,
                                        borderRadius: '50%',
                                    }}>
                                        <DisabledWrapper style={{
                                            boxShadow: `inset 0px -8.58025px 22.2228px -3.4321px #FFFFFF, inset 0px -11.1543px 27.4568px -23.1667px #5282FF, inset 0px -17.1605px 14.4148px -6.00617px #1741B1, inset 0px 4px 14px #5690FF`
                                        }}>

                                        </DisabledWrapper>
                                        <ReactPlayerWrapper videoSrc={S3_FILES.YAML_HELPER_VIDEO}
                                                            posterSrc={S3_FILES.YAML_HELPER_VIDEO_POSTER}
                                                            style={{zIndex: 1}}/>
                                    </DisabledWrapper>
                                </div>
                                <div style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: r.vmax(20),
                                    whiteSpace: 'pre-wrap',
                                    fontSize: r.vmax(28),
                                    lineHeight: r.vmax(32),
                                }}>
                                    <p>{`Подсказка от YAML Oracle:`}</p>
                                    {hint?.map((item, i) => {
                                        return (
                                            <p key={i}>{item}</p>
                                        )
                                    })}
                                </div>
                            </div>
                            <ButtonReforge enableGlowEffect={false} onClick={() => onClose()} minHeight={110}
                                           height={110} style={{
                                background: '#5A5A5A10',
                                boxShadow: 'inset 0px -26px 34px rgba(255, 255, 255, 0.16), inset 0px 5px 50px rgba(255, 255, 255, 0.2)'
                            }}>
                                <ButtonSpan>Ок, понятно</ButtonSpan>
                                <ShortcutReforge isLarge shadowColor="#9CB7FF">ENTER</ShortcutReforge>
                            </ButtonReforge>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

const BotHelperButton: React.FC<BotHelperButtonProps> = ({className, hint, ...props}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    useShortcuts({
        onCtrlI: () => setIsModalOpen(true),
        onEnter: () => {
            if (isModalOpen) {
                setIsModalOpen(false);
            }
        },
        enabledSections: ['briefing', 'editor']
    });

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsModalOpen(true);
        if (props.onClick) {
            props.onClick(e);
        }
    };

    return (
        <>
            <button
                {...props}
                onClick={handleClick}
                style={{
                    cursor: "pointer",
                    position: 'absolute',
                    bottom: r.vmax(1),
                    right: r.vmax(0),
                    width: r.vmax(245),
                    height: r.vmax(92),
                    paddingLeft: r.vmax(62),
                    background: 'rgba(138, 155, 255, 0.2)',
                    zIndex: 6,
                    color: 'white',
                    fontSize: r.vmax(16),
                    lineHeight: r.vmax(20),
                    whiteSpace: 'pre-wrap',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    gap: r.vmax(6),
                    alignItems: 'start',
                    boxShadow: `inset 0 ${r.vmax(5)} ${r.vmax(50)} #FFFFFF, inset 0 ${r.vmax(-26)} ${r.vmax(58)} #5282FF, inset 0 ${r.vmax(-23)} ${r.vmax(20)} #1741B1`,
                    backdropFilter: `blur(${r.vmax(82)})`,
                    border: 'none',
                }}
            >
                <div style={{
                    width: r.vmax(92),
                    height: r.vmax(92),
                    position: 'absolute',
                    top: 0,
                    background: '#110B29',
                    left: r.vmax(-46),
                    borderRadius: '50%',
                    boxShadow: `0px 3.4321px 20.5926px #537EFF, 0px 3.4321px 46.3333px #537EFF`
                }}>
                    <DisabledWrapper style={{
                        inset: 2,
                        zIndex: 1,
                        borderRadius: '50%',
                        boxShadow: `inset 0px -8.58025px 22.2228px -3.4321px #FFFFFF, inset 0px -11.1543px 27.4568px -23.1667px #5282FF, inset 0px -17.1605px 14.4148px -6.00617px #1741B1`
                    }}>
                        <ReactPlayerWrapper videoSrc={S3_FILES.YAML_HELPER_VIDEO}
                                            posterSrc={S3_FILES.YAML_HELPER_VIDEO_POSTER}
                                            style={{zIndex: 1}}/>
                    </DisabledWrapper>

                    <DisabledWrapper style={{
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: `linear-gradient(180deg, #5282FF 0%, #F5F8FF 10.08%, #5282FF 23.95%, #F5F8FF 37.81%, #5282FF 54.42%, #F5F8FF 73.03%, #5282FF 97.6%)`
                    }}></DisabledWrapper>
                </div>
                <span style={{
                    textShadow: `0 ${r.vmax(4)} ${r.vmax(30)} #5282FF`,
                    textAlign: 'left',
                    fontSize: r.vmax(18),
                    lineHeight: r.vmax(24),
                }}>{`Подсказка`}</span>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: r.vmax(4),
                }}>
                    <ShortcutReforge>{getModifierKey()}</ShortcutReforge>
                    <span style={{
                        textShadow: `0 ${r.vmax(4)} ${r.vmax(30)} #5282FF`,
                        textAlign: 'left',
                        fontSize: r.vmax(18),
                        lineHeight: r.vmax(24),
                    }}>{`+`}</span>
                    <ShortcutReforge>I</ShortcutReforge>
                </div>
            </button>
            <BotHelperModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                hint={hint}
            />
        </>
    );
};

export default BotHelperButton;
